/**
 * Nina Engine API Client (Runde 2).
 *
 * Kein globales Store-Framework, keine externen Abhaengigkeiten außer fetch.
 * SSE-Streaming über ReadableStream + TextDecoder.
 */

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const BASE = `${BACKEND}/api/nina-engine`;

function authHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function req(path, { method = "GET", body, signal } = {}) {
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(),
    },
    body: body ? JSON.stringify(body) : undefined,
    signal,
  });
  const text = await res.text();
  const data = text ? safeJson(text) : null;
  if (!res.ok) {
    const err = new Error((data && (data.detail || data.error)) || `HTTP ${res.status}`);
    err.status = res.status;
    err.body = data;
    throw err;
  }
  return data;
}

function safeJson(t) {
  try {
    return JSON.parse(t);
  } catch {
    return { raw: t };
  }
}

export const ninaEngine = {
  // Meta
  health: () => req("/health"),
  access: () => req("/access"),
  serverTime: () => req("/time"),
  stats: () => req("/stats"),

  // Conversations
  listConversations: (includeArchived = false) =>
    req(`/conversations?include_archived=${includeArchived ? "true" : "false"}`),
  createConversation: (payload = {}) => req("/conversations", { method: "POST", body: payload }),
  getMessages: (convId) => req(`/conversations/${convId}/messages`),
  renameConversation: (convId, title) =>
    req(`/conversations/${convId}`, { method: "PATCH", body: { title } }),
  archiveConversation: (convId, archived = true) =>
    req(`/conversations/${convId}`, {
      method: "PATCH",
      body: { status: archived ? "archived" : "active" },
    }),
  setConversationScope: (convId, scope) =>
    req(`/conversations/${convId}`, { method: "PATCH", body: { scope } }),
  deleteConversation: (convId) => req(`/conversations/${convId}`, { method: "DELETE" }),
  markConversationRead: (convId) =>
    req(`/conversations/${convId}/mark-read`, { method: "POST", body: {} }),

  // Non-streaming (Fallback)
  chat: (message, conversationId, preferFast = false) =>
    req("/chat", {
      method: "POST",
      body: { message, conversation_id: conversationId, prefer_fast: preferFast },
    }),

  // Insights
  listInsights: (status = "pending") => req(`/insights?status=${status}`),
  updateInsight: (id, patch) => req(`/insights/${id}`, { method: "PATCH", body: patch }),
  resolveInsight: (id, decision) =>
    req(`/insights/${id}/resolve`, { method: "POST", body: { decision } }),

  runReflection: () => req("/reflection/run", { method: "POST", body: { force: true } }),

  // TGAone Tool Layer — Integrations-/Statusseite
  tgaoneStatus: () => req("/tools/tgaone/status"),
  tgaoneStatusTest: () => req("/tools/tgaone/status/test", { method: "POST" }),
  driveStatus: () => req("/tools/drive/status"),

  // Drive Projekt-Mapping & Sync (11.08.2026)
  driveGetExternalRoot: () => req("/tools/drive/settings/external-root"),
  driveSetExternalRoot: (folderId) =>
    req("/tools/drive/settings/external-root", { method: "PUT", body: { folder_id: folderId } }),
  driveClearExternalRoot: () =>
    req("/tools/drive/settings/external-root", { method: "DELETE" }),
  driveMappingCandidates: (limit = 500) =>
    req(`/tools/drive/mapping/candidates?limit=${limit}`),
  driveMappingConfirm: (projectId, folderId) =>
    req("/tools/drive/mapping/confirm", { method: "POST", body: { project_id: projectId, folder_id: folderId } }),
  driveMappingClear: (projectId) =>
    req(`/tools/drive/mapping/${projectId}`, { method: "DELETE" }),
  driveSyncProject: (projectId) =>
    req(`/tools/drive/projects/${projectId}/sync`, { method: "POST" }),
  driveSyncAll: () => req("/tools/drive/sync-all", { method: "POST" }),

  // Chat-Organisation
  getSidebarTree: () => req("/sidebar-tree"),
  provisionProjectChats: () => req("/conversations/provision-project-chats", { method: "POST" }),
  pinConversation: (id, isPinned) =>
    req(`/conversations/${id}/pin`, { method: "POST", body: { is_pinned: isPinned } }),
  moveConversation: (id, folderId) =>
    req(`/conversations/${id}/move`, { method: "POST", body: { folder_id: folderId } }),
  listFolders: () => req("/folders"),
  createFolder: (name, parent_id = null) =>
    req("/folders", { method: "POST", body: { name, parent_id } }),
  updateFolder: (id, patch) => req(`/folders/${id}`, { method: "PATCH", body: patch }),
  deleteFolder: (id) => req(`/folders/${id}`, { method: "DELETE" }),
  createGroupChat: (payload) =>
    req("/conversations/group", { method: "POST", body: payload }),
  getParticipants: (id) =>
    req(`/conversations/${id}/participants`),
  updateParticipants: (id, patch) =>
    req(`/conversations/${id}/participants`, { method: "PATCH", body: patch }),
  listUsersDirectory: async () => {
    // Directory endpoint ist außerhalb des nina-engine Prefix
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const token = localStorage.getItem("token");
    const r = await fetch(`${base}/api/users/directory`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) throw new Error(`users/directory ${r.status}`);
    return r.json();
  },

  // Nina Chat — Projekt-Kontext + Zusammenfassen
  openProjectConversation: (projectId) =>
    req(`/conversations/for-project/${projectId}`, { method: "POST" }),
  summarizeConversations: (conversation_ids, title) =>
    req("/conversations/summarize", {
      method: "POST",
      body: { conversation_ids, title: title || null },
    }),

  // Muster-Vorlage + Sync SSE
  driveTemplateCandidates: () => req("/tools/drive/template/candidates"),
  driveTemplateTree: (folderId, maxDepth = 4) =>
    req(`/tools/drive/template/tree/${folderId}?max_depth=${maxDepth}`),
  driveGetProjectTemplate: () => req("/tools/drive/settings/project-template"),
  driveSetProjectTemplate: (folderId) =>
    req("/tools/drive/settings/project-template", { method: "PUT", body: { folder_id: folderId } }),
  driveClearProjectTemplate: () =>
    req("/tools/drive/settings/project-template", { method: "DELETE" }),
  driveSyncAllStream: async ({ limit = 200, onProgress, onStarted, onDone, onError, signal } = {}) => {
    const res = await fetch(`${BASE}/tools/drive/sync-all/stream?limit=${limit}`, {
      method: "GET",
      headers: { ...authHeaders(), Accept: "text/event-stream" },
      signal,
    });
    if (!res.ok || !res.body) {
      const err = new Error(`HTTP ${res.status}`);
      err.status = res.status;
      onError && onError(err);
      throw err;
    }
    const reader = res.body.getReader();
    const dec = new TextDecoder();
    let buf = "";
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      const chunks = buf.split(/\n\n/);
      buf = chunks.pop() || "";
      for (const c of chunks) {
        let event = "message", data = null;
        for (const line of c.split(/\n/)) {
          if (line.startsWith("event:")) event = line.slice(6).trim();
          else if (line.startsWith("data:")) {
            const raw = line.slice(5).trim();
            try { data = JSON.parse(raw); } catch { /* ignore */ }
          }
        }
        if (event === "started" && onStarted) onStarted(data);
        else if (event === "progress" && onProgress) onProgress(data);
        else if (event === "done" && onDone) onDone(data);
      }
    }
  },

  /**
   * SSE-Streaming Chat.
   * @param {{ message: string, conversationId?: string, preferFast?: boolean, signal?: AbortSignal }}
   * @param {{ onStart?, onDelta?, onDone?, onError? }} handlers
   * @returns {Promise<void>}
   */
  streamChat: async (
    { message, conversationId, preferFast = false, confirmedTools, attachments, signal },
    { onStart, onDelta, onDone, onError, onToolStart, onToolResult, onStatus } = {}
  ) => {
    const res = await fetch(`${BASE}/chat/stream`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        ...authHeaders(),
      },
      body: JSON.stringify({
        message,
        conversation_id: conversationId,
        prefer_fast: preferFast,
        confirmed_tools: confirmedTools || null,
        attachments: attachments || null,
      }),
      signal,
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`stream failed: HTTP ${res.status} ${text.slice(0, 200)}`);
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buffer = "";
    let currentEvent = null;
    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        // Parse line by line
        while ((idx = buffer.indexOf("\n")) !== -1) {
          const line = buffer.slice(0, idx).replace(/\r$/, "");
          buffer = buffer.slice(idx + 1);
          if (!line) {
            currentEvent = null;
            continue;
          }
          if (line.startsWith("event:")) {
            currentEvent = line.slice(6).trim();
          } else if (line.startsWith("data:")) {
            const raw = line.slice(5).trim();
            let payload = {};
            try {
              payload = JSON.parse(raw);
            } catch {
              /* ignore */
            }
            if (currentEvent === "start" && onStart) onStart(payload);
            else if (currentEvent === "delta" && onDelta) onDelta(payload);
            else if (currentEvent === "status" && onStatus) onStatus(payload);
            else if (currentEvent === "tool_start" && onToolStart) onToolStart(payload);
            else if (currentEvent === "tool_result" && onToolResult) onToolResult(payload);
            else if (currentEvent === "done" && onDone) onDone(payload);
            else if (currentEvent === "error" && onError) onError(payload);
          }
        }
      }
    } catch (err) {
      if (err.name === "AbortError") {
        if (onDone) onDone({ aborted: true, assistant_message: null, memory_saved: { facts: 0, episodes: 0 } });
        return;
      }
      throw err;
    }
  },
};

export default ninaEngine;
